import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ImageManager } from '@/lib/imageManager';
import { validateFileUpload, sanitizeFileName } from '@/lib/security';

interface ImageUploadProps {
  currentImageUrl?: string;
  onImageChange: (imageUrl: string) => void;
  bucketName: string;
  folder?: string;
  label: string;
  accept?: string;
  useImageUtils?: boolean; // Whether to use getCategoryImageUrl for display
}

export const ImageUpload = ({ 
  currentImageUrl, 
  onImageChange, 
  bucketName, 
  folder = '', 
  label,
  accept = "image/*",
  useImageUtils = false
}: ImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadImage = async (file: File) => {
    try {
      setUploading(true);
      
      // Generate secure filename
      const fileExt = file.name.split('.').pop();
      const sanitizedOriginalName = sanitizeFileName(file.name.replace(/\.[^/.]+$/, ""));
      const fileName = `${sanitizedOriginalName}-${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = folder ? `${folder}/${fileName}` : fileName;

      // Upload file to Supabase Storage
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file);

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      onImageChange(urlData.publicUrl);
      toast.success('התמונה הועלתה בהצלחה');

    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error('שגיאה בהעלאת התמונה: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Use comprehensive security validation
      const validation = validateFileUpload(file);
      if (!validation.isValid) {
        toast.error(validation.error || 'קובץ לא תקין');
        return;
      }

      uploadImage(file);
    }
  };

  const removeImage = () => {
    onImageChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      
      {/* Current Image Display */}
      {currentImageUrl && (
         <div className="relative inline-block">
           <img
             src={useImageUtils ? ImageManager.getImageUrl(currentImageUrl, 'category') : currentImageUrl}
             alt="תצוגה מקדימה"
             className="w-32 h-32 object-cover rounded-lg border"
             onError={(e) => {
               const target = e.target as HTMLImageElement;
               target.src = '/images/placeholder-category-sketch.jpg';
             }}
           />
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full p-0"
            onClick={removeImage}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Upload Button */}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2"
        >
          {uploading ? (
            <>
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              מעלה...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              {currentImageUrl ? 'החלף תמונה' : 'העלה תמונה'}
            </>
          )}
        </Button>
        
        <div className="text-xs text-muted-foreground">
          JPG, PNG, WEBP עד 5MB
        </div>
      </div>

      {/* Hidden File Input */}
      <Input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};